import { Camera, CameraOff, Maximize2, ScanFace, Mic, MicOff, MonitorOff, MousePointer2, MousePointerClick, PhoneOff, Play, Radio, Volume2, VolumeX } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { blurChoice, blurTrack, canBlur, saveBlurChoice, type BlurredTrack } from '../lib/blur';
import { PcmPlayer } from '../lib/pcmPlayer';
import { SLOTS, type SignalData } from '../lib/rtc';
import type { Child, Device } from '../lib/types';
import { useParent } from './ParentContext';
import { PointerLayer, PointerOptions, usePointerStyle } from './PointerControls';
import { ControlLayer } from './RemoteControl';
import { childHas } from './Plans';

const HEAR_MIC_KEY = 'cg.live.hearMic';

type State = 'idle' | 'connecting' | 'live';

/**
 * Live view of a child's computer. With `voiceOnly` (#863) it's only the talking: the same connection runs behind the
 * whiteboard with the screen hidden but its sound still playing, and a slim bar to connect, talk, listen and hang up.
 */
export function LiveView({ child, device, voiceOnly = false }: { child: Child; device: Device; voiceOnly?: boolean }) {
  const { socket, toast, parent } = useParent();
  // Camera blur (#592): blur the parent's room before their camera is sent, when the admin offers it.
  const blurOffered = (parent.cameraBlur?.offered ?? false) && canBlur();
  const [blurMine, setBlurMineState] = useState(() => blurChoice('cg.blurMine') ?? parent.cameraBlur?.byDefault ?? false);
  const setBlurMine = (on: boolean) => {
    saveBlurChoice('cg.blurMine', on);
    setBlurMineState(on);
  };
  const [state, setState] = useState<State>('idle');
  const [error, setError] = useState('');
  // Listening to the student's microphone starts off (#294): a parent near their child's computer would otherwise hear
  // the room echo back and forth between the two devices. The choice is remembered on this device.
  const [hearMic, setHearMicState] = useState(() => {
    try {
      return localStorage.getItem(HEAR_MIC_KEY) === '1';
    } catch {
      return false;
    }
  });
  const setHearMic = (update: (v: boolean) => boolean) =>
    setHearMicState((v) => {
      const next = update(v);
      try {
        localStorage.setItem(HEAR_MIC_KEY, next ? '1' : '0');
      } catch {
        /* storage unavailable */
      }
      return next;
    });
  // The child's computer sound starts off too (#930), like their mic and camera: a fresh connection is quiet until the
  // parent turns something on.
  const [hearAudio, setHearAudio] = useState(false);
  // Viewing the student's camera starts off every time (#930): with it off, live view is screen-only, so ghost mode
  // (#928) applies and the student's camera stays dark. Turning it on asks their computer to send the camera.
  const [watchCam, setWatchCam] = useState(false);
  const [holding, setHolding] = useState(false);
  const [openMic, setOpenMic] = useState(false);
  const [camOn, setCamOn] = useState(false);
  const [pointerOn, setPointerOn] = useState(false);
  const [relay, setRelay] = useState(false);
  const [pointerStyle, setPointerStyle] = usePointerStyle();
  const sendPointer = useCallback((m: object) => socket?.send({ type: 'pointer', deviceId: device.id, ...m }), [socket, device.id]);
  // Interact (#306): control the computer's mouse and keyboard. Only the Windows and Mac apps can do it.
  const [interactOn, setInteractOn] = useState(false);
  // The web /agent page reports no app version; and Interact must be in the family's plan (#99).
  const canInteract = device.kind === 'agent' && !!device.appVersion && childHas(child, 'interact');
  const sendInput = useCallback((m: object) => socket?.send({ type: 'input', deviceId: device.id, ...m }), [socket, device.id]);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const slots = useRef<RTCRtpTransceiver[]>([]);
  const queue = useRef<Promise<void>>(Promise.resolve());
  const local = useRef<{ audio?: MediaStreamTrack; video?: MediaStreamTrack; blurred?: BlurredTrack }>({});
  const stageRef = useRef<HTMLDivElement>(null);
  // The cameras sit in a strip above the screen, never over it (#593); full screen takes the strip along.
  const wrapRef = useRef<HTMLDivElement>(null);
  const [camsShown, setCamsShown] = useState(() => {
    try {
      return localStorage.getItem('cg.liveCams') !== 'hidden';
    } catch {
      return true;
    }
  });
  const toggleCams = () =>
    setCamsShown((v) => {
      try {
        localStorage.setItem('cg.liveCams', v ? 'hidden' : 'shown');
      } catch {
        /* private mode: just this visit */
      }
      return !v;
    });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const relayRef = useRef(false);
  // Reassembly buffer for chunked relay frames (#145): the current frame's parts until it's complete.
  const relayBuf = useRef<{ id: number; n: number; parts: string[]; got: number } | null>(null);
  const fallbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const screenRef = useRef<HTMLVideoElement>(null);
  const camRef = useRef<HTMLVideoElement>(null);
  const micRef = useRef<HTMLAudioElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const selfRef = useRef<HTMLVideoElement>(null);
  // A Mac sends its computer sound over a data channel instead of the audio slot (#294).
  const isMac = /mac/i.test(device.platform ?? '');
  const pcmRef = useRef<PcmPlayer | null>(null);
  const hearAudioRef = useRef(hearAudio);
  hearAudioRef.current = hearAudio;

  const status = device.status;
  // The parent's microphone is on: held down, or Open mic.
  const talking = holding || openMic;
  // Open mic (#574): your voice is heard right now. Hold to talk counts as speaking the whole time it's held.
  const [voice, setVoice] = useState(false);
  const [micStream, setMicStream] = useState<MediaStream | null>(null);
  const speaking = holding || (openMic && voice);
  // While the parent speaks, the student's mic and computer sound don't play here: their own voice comes back in those
  // (from the child's speakers), and a phone's mic would pick it up again, echoing over and over. One side talks at a
  // time, like the student's computer pausing its mic (#294). With Open mic that's only while the parent's voice is
  // heard, so the student can answer in the pauses (#574).
  useEffect(() => pcmRef.current?.setMuted(!hearAudio || speaking), [hearAudio, speaking]);
  useEffect(() => {
    if (!openMic || holding || !micStream) {
      setVoice(false);
      return;
    }
    const ctx = new AudioContext();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    ctx.createMediaStreamSource(micStream).connect(analyser);
    const buf = new Float32Array(analyser.fftSize);
    let lastHeard = 0;
    const tick = setInterval(() => {
      analyser.getFloatTimeDomainData(buf);
      let sum = 0;
      for (const v of buf) sum += v * v;
      const level = Math.sqrt(sum / buf.length);
      const now = Date.now();
      if (level > 0.03) lastHeard = now;
      // Keep counting as speaking for a moment after the voice drops, so short pauses between words don't flap.
      setVoice(now - lastHeard < 900);
    }, 80);
    return () => {
      clearInterval(tick);
      void ctx.close();
    };
  }, [openMic, holding, micStream]);
  // The student's computer opens its microphone only while we're listening (#294) and its camera only while we're
  // viewing it (#930): tell it when live view connects and whenever either toggle changes. Both start off, so a fresh
  // connection is screen-only (ghost mode applies until the parent asks for the camera or mic).
  useEffect(() => {
    if (state === 'live' && pcRef.current) socket?.send({ type: 'listen', deviceId: device.id, mic: hearMic, camera: watchCam });
  }, [state, hearMic, watchCam, socket, device.id]);
  // A fresh connection always starts with the camera off (#930), whatever it was last time.
  useEffect(() => {
    if (state !== 'live') setWatchCam(false);
  }, [state]);

  // Tell the computer when Interact turns on, and off again when it's switched off or live view ends.
  useEffect(() => {
    if (state !== 'live' || !interactOn || !socket) return;
    socket.send({ type: 'control', deviceId: device.id, on: true });
    return () => {
      socket.send({ type: 'control', deviceId: device.id, on: false });
    };
  }, [state, interactOn, socket, device.id]);

  const stop = useCallback(
    (tellDevice = true) => {
      if (fallbackTimer.current) {
        clearTimeout(fallbackTimer.current);
        fallbackTimer.current = null;
      }
      if (tellDevice && pcRef.current) socket?.send({ type: 'hangup', deviceId: device.id });
      if (tellDevice && relayRef.current) socket?.send({ type: 'relay-stop', deviceId: device.id });
      relayRef.current = false;
      relayBuf.current = null;
      setRelay(false);
      pcRef.current?.close();
      pcRef.current = null;
      slots.current = [];
      pcmRef.current?.close();
      pcmRef.current = null;
      local.current.audio?.stop();
      local.current.video?.stop();
      local.current.blurred?.stop();
      local.current = {};
      for (const el of [screenRef.current, camRef.current, micRef.current, audioRef.current, selfRef.current]) {
        if (el) el.srcObject = null;
      }
      setHolding(false);
      setOpenMic(false);
      setCamOn(false);
      setPointerOn(false);
      setInteractOn(false);
      setState('idle');
    },
    [socket, device.id],
  );

  // The server-relay screen path (used when WebRTC can't connect, e.g. on a hotel network): the child
  // sends JPEG frames over the WebSocket and we paint them to a canvas. No talk/camera/pointer in this mode.
  const startRelay = useCallback(() => {
    if (!socket) return;
    setError('');
    relayRef.current = true;
    setRelay(true);
    setState('live');
    socket.send({ type: 'relay-start', deviceId: device.id });
  }, [socket, device.id]);

  const start = useCallback(async () => {
    if (!socket) return;
    // There's no Relay view button (#354): live view falls back to the relay on its own. Tests (and support) can still go
    // straight to it with localStorage cg.live.forceRelay = '1'.
    try {
      if (localStorage.getItem('cg.live.forceRelay') === '1') return startRelay();
    } catch {
      /* storage unavailable */
    }
    setError('');
    setState('connecting');
    try {
      const { iceServers } = await api.get<{ iceServers: RTCIceServer[] }>('/api/ice');
      const pc = new RTCPeerConnection({ iceServers });
      pcRef.current = pc;
      slots.current = SLOTS.map((s) => pc.addTransceiver(s.kind, { direction: s.from === 'child' ? 'recvonly' : 'sendonly' }));
      const show = (el: HTMLMediaElement | null, i: number) => {
        if (el) el.srcObject = new MediaStream([slots.current[i]!.receiver.track]);
      };
      show(screenRef.current, 0);
      show(camRef.current, 1);
      show(micRef.current, 2);
      show(audioRef.current, 3);
      pc.onicecandidate = (e) =>
        socket.send({ type: 'signal', deviceId: device.id, data: { candidate: e.candidate ? e.candidate.toJSON() : null } });
      // Fall back to the server relay when the direct connection can't be made — both on an outright
      // failure and when it just never connects (common off-LAN without a TURN server, where ICE stalls
      // in 'checking'/'disconnected' instead of ever reaching 'failed').
      const toRelay = (why: string) => {
        if (pc !== pcRef.current) return;
        if (fallbackTimer.current) {
          clearTimeout(fallbackTimer.current);
          fallbackTimer.current = null;
        }
        pc.close();
        pcRef.current = null;
        slots.current = [];
        setError(`${why} — switched to relay mode (screen only; works on restricted networks).`);
        startRelay();
      };
      pc.onconnectionstatechange = () => {
        if (pc !== pcRef.current) return;
        if (pc.connectionState === 'connected') {
          if (fallbackTimer.current) {
            clearTimeout(fallbackTimer.current);
            fallbackTimer.current = null;
          }
          setState('live');
        }
        if (pc.connectionState === 'failed') toRelay('Direct connection failed');
      };
      if (isMac) {
        // Started here, inside the click that began live view, so the browser lets it play.
        pcmRef.current?.close();
        const player = new PcmPlayer();
        player.setMuted(!hearAudioRef.current);
        pcmRef.current = player;
        const ch = pc.createDataChannel('sysaudio', { ordered: false, maxRetransmits: 0 });
        ch.binaryType = 'arraybuffer';
        ch.onmessage = (e) => {
          if (e.data instanceof ArrayBuffer) pcmRef.current?.push(e.data);
        };
      }
      await pc.setLocalDescription(await pc.createOffer());
      socket.send({ type: 'signal', deviceId: device.id, data: { description: pc.localDescription!.toJSON() } });
      fallbackTimer.current = setTimeout(() => {
        if (pcRef.current === pc && pc.connectionState !== 'connected') toRelay('Direct connection is taking too long');
      }, 9000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Couldn’t start the live view.');
      stop(false);
    }
  }, [socket, device.id, stop, startRelay, isMac]);

  // Answers, network details and hang-ups from the child's device.
  useEffect(() => {
    if (!socket) return;
    const offs = [
      socket.on('signal', (m) => {
        if (m.deviceId !== device.id) return;
        const data = m.data as SignalData;
        queue.current = queue.current.then(async () => {
          const pc = pcRef.current;
          if (!pc) return;
          try {
            if (data.description) await pc.setRemoteDescription(data.description);
            else if (data.candidate) await pc.addIceCandidate(data.candidate);
          } catch (e) {
            console.warn('signal failed', e);
          }
        });
      }),
      socket.on('error', (m) => {
        if (m.deviceId !== device.id) return;
        setError(String(m.error));
        stop(false);
      }),
      socket.on('hangup', (m) => {
        if (m.deviceId !== device.id || !pcRef.current) return;
        stop(false);
        if (m.reason) setError(String(m.reason));
      }),
      socket.on('relay-frame', (m) => {
        if (m.deviceId !== device.id || !relayRef.current) return;
        // Collect the frame's chunks; a new frameId drops any incomplete previous frame.
        const id = Number(m.frameId) || 0;
        const n = Math.max(1, Number(m.n) || 1);
        const i = Math.max(0, Number(m.i) || 0);
        let buf = relayBuf.current;
        if (!buf || buf.id !== id) relayBuf.current = buf = { id, n, parts: new Array(n).fill(''), got: 0 };
        if (i >= buf.n || buf.parts[i]) return; // out of range or a duplicate chunk
        buf.parts[i] = String(m.data ?? '');
        buf.got += 1;
        if (buf.got < buf.n) return; // wait for the rest of this frame
        relayBuf.current = null;
        const b64 = buf.parts.join('');
        const canvas = canvasRef.current;
        if (!canvas || !b64) return;
        const img = new Image();
        img.onload = () => {
          const w = Number(m.w) || img.width;
          const h = Number(m.h) || img.height;
          if (canvas.width !== w) canvas.width = w;
          if (canvas.height !== h) canvas.height = h;
          canvas.getContext('2d')?.drawImage(img, 0, 0, w, h);
        };
        img.src = `data:image/jpeg;base64,${b64}`;
      }),
    ];
    return () => offs.forEach((off) => off());
  }, [socket, device.id, stop]);

  useEffect(() => () => stop(), [stop]);

  // Live view ends when school time ends or a break starts.
  useEffect(() => {
    if (!child.policy.monitoring && pcRef.current) stop(false);
  }, [child.policy.monitoring, stop]);

  useEffect(() => {
    if (!device.online && pcRef.current) {
      stop(false);
      setError(`${device.name} went offline.`);
    }
  }, [device.online, device.name, stop]);

  // The parent's microphone and camera go into their slots only while in use.
  useEffect(() => {
    const pc = pcRef.current;
    if (!pc || state === 'idle') return;
    let cancelled = false;
    (async () => {
      try {
        if (talking && !local.current.audio) {
          const s = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
          local.current.audio = s.getAudioTracks()[0];
          setMicStream(s);
        }
        if (camOn && !local.current.video) {
          const s = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480, facingMode: 'user' } });
          local.current.video = s.getVideoTracks()[0];
        }
        // Blur (#592): the blurred copy is what's sent and previewed; it's ready before anything is sent.
        const wantBlur = camOn && blurOffered && blurMine;
        if (wantBlur && !local.current.blurred && local.current.video) {
          try {
            local.current.blurred = await blurTrack(local.current.video);
          } catch {
            toast({ text: 'Background blur isn’t available in this browser.', tone: 'alert' });
            setBlurMine(false);
            return;
          }
        }
        if (!wantBlur && local.current.blurred) {
          local.current.blurred.stop();
          local.current.blurred = undefined;
        }
        if (cancelled || pc !== pcRef.current) return;
        const sent = camOn ? (local.current.blurred?.track ?? local.current.video ?? null) : null;
        await slots.current[4]?.sender.replaceTrack(talking ? (local.current.audio ?? null) : null);
        await slots.current[5]?.sender.replaceTrack(sent);
        if (selfRef.current && (selfRef.current.srcObject as MediaStream | null)?.getVideoTracks()[0] !== sent) {
          selfRef.current.srcObject = sent ? new MediaStream([sent]) : null;
        }
        if (!camOn && local.current.video) {
          local.current.video.stop();
          local.current.video = undefined;
        }
        socket?.send({ type: 'talk', deviceId: device.id, on: talking, speaking, video: camOn });
      } catch {
        toast({ text: 'Allow the microphone or camera in your browser to talk.', tone: 'alert' });
        setHolding(false);
        setOpenMic(false);
        setCamOn(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [talking, camOn, blurMine, state, socket, device.id, toast]); // eslint-disable-line react-hooks/exhaustive-deps
  // Open mic (#574): tell the student's computer when the parent starts and stops speaking, so it pauses its mic only then.
  useEffect(() => {
    if (state === 'idle' || !talking) return;
    socket?.send({ type: 'talk', deviceId: device.id, on: talking, speaking, video: camOn });
  }, [speaking]); // eslint-disable-line react-hooks/exhaustive-deps

  const fullscreen = () => {
    // iOS Safari doesn't support fullscreen on a <div> (requestFullscreen is undefined there) — only on
    // a <video> via webkitEnterFullscreen. So try the stage element first (desktop/Android), then fall
    // back to the screen video for iOS. Also handle the webkit-prefixed exit.
    const stage = wrapRef.current as (HTMLDivElement & { webkitRequestFullscreen?: () => void }) | null;
    const vid = screenRef.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null;
    const doc = document as Document & { webkitFullscreenElement?: Element; webkitExitFullscreen?: () => void };
    if (doc.fullscreenElement || doc.webkitFullscreenElement) {
      (doc.exitFullscreen ?? doc.webkitExitFullscreen)?.call(doc);
      return;
    }
    if (stage?.requestFullscreen) stage.requestFullscreen().catch(() => undefined);
    else if (stage?.webkitRequestFullscreen) stage.webkitRequestFullscreen();
    else vid?.webkitEnterFullscreen?.();
  };

  const live = state !== 'idle';
  // A voice call is for talking with the child, so hear their microphone while it's on (not remembered as the choice).
  useEffect(() => {
    if (voiceOnly && state === 'live') setHearMicState(true);
  }, [voiceOnly, state]);
  // The student's camera shows only while we're viewing it (#930) and their computer is sending it.
  const childCam = !relay && watchCam && !!status?.camera;
  const allowed = child.policy.monitoring;

  return (
    <section className={voiceOnly ? 'live live-voice' : 'live'}>
      {voiceOnly && (
        <div className="voice-bar" role="group" aria-label={`Talk with ${child.name}`}>
          {!live ? (
            !device.online ? (
              <span className="muted small">{device.name} is offline, so you can’t talk right now.</span>
            ) : !allowed ? (
              <span className="muted small">Talking opens during school time{child.policy.onBreak ? ', after the break' : ''}.</span>
            ) : (
              <button className="btn primary" onClick={start}>
                <Mic size={18} /> Talk with {child.name}
              </button>
            )
          ) : state === 'connecting' ? (
            <span className="muted small">Connecting to {device.name}…</span>
          ) : relay ? (
            <>
              <span className="muted small">This network can’t carry voice; open Live view for the relay.</span>
              <button className="chip danger" onClick={() => stop()}>
                <PhoneOff size={16} /> Hang up
              </button>
            </>
          ) : (
            <>
              <span className="voice-who">
                <span className="dot on" /> {speaking ? `${child.name} hears you` : `Talking with ${child.name}`}
              </span>
              <button
                className={`btn talk ${holding ? 'active' : ''}`}
                onPointerDown={(e) => {
                  e.currentTarget.setPointerCapture(e.pointerId);
                  setHolding(true);
                }}
                onPointerUp={() => setHolding(false)}
                onPointerCancel={() => setHolding(false)}
                onContextMenu={(e) => e.preventDefault()}
              >
                <Mic size={18} /> {holding ? 'Talking…' : 'Hold to talk'}
              </button>
              <button className={`chip ${openMic ? 'active' : ''}`} onClick={() => setOpenMic((v) => !v)} aria-pressed={openMic}>
                {openMic ? <Mic size={16} /> : <MicOff size={16} />} Open mic
              </button>
              <button className={`chip ${hearMic ? 'active' : ''}`} onClick={() => setHearMicState((v) => !v)} aria-pressed={hearMic} disabled={!status?.mic}>
                {hearMic ? <Volume2 size={16} /> : <VolumeX size={16} />} Hear {child.name}
              </button>
              <button className="chip danger" onClick={() => stop()}>
                <PhoneOff size={16} /> Hang up
              </button>
            </>
          )}
        </div>
      )}
      {voiceOnly && live && <p className="muted small voice-note">{child.name} sees the “watching” notice while you’re connected.</p>}
      {/* With voiceOnly the screen is out of sight, but its audio elements keep playing. */}
      <div className="stage-wrap" ref={wrapRef} style={voiceOnly ? { display: 'none' } : undefined}>
        {/* Always mounted, so the video elements are there when the call connects; hidden until a camera is on. */}
        <div className={`cam-strip ${live && !relay && (childCam || camOn) ? '' : 'hidden'}`}>
          <figure className={`cam-tile ${camsShown && childCam ? '' : 'hidden'}`}>
            <video ref={camRef} className="cam" autoPlay playsInline muted />
            <figcaption>{status?.blurred ? `${child.name} blurred their background` : child.name}</figcaption>
          </figure>
          <figure className={`cam-tile ${camsShown && camOn ? '' : 'hidden'}`}>
            <video ref={selfRef} className="self" autoPlay playsInline muted />
            <figcaption>You</figcaption>
          </figure>
          <button className="chip small cam-strip-toggle" onClick={toggleCams} aria-pressed={!camsShown}>
            {camsShown ? 'Hide cameras' : 'Show cameras'}
          </button>
        </div>
      <div className={`stage ${live ? 'on' : ''}`} ref={stageRef}>
        <video ref={screenRef} className={`screen ${relay ? 'hidden' : ''}`} autoPlay playsInline muted />
        <canvas ref={canvasRef} className={`screen relay-canvas ${relay ? '' : 'hidden'}`} />
        <audio ref={micRef} autoPlay muted={!hearMic || speaking} />
        <audio ref={audioRef} autoPlay muted={!hearAudio || speaking} />

        {!live && (
          <div className="stage-cover">
            {!device.online ? (
              <p>{device.name} is offline. Open ClassGuardian on it to watch live.</p>
            ) : !allowed ? (
              <p>Live view opens during school time{child.policy.onBreak ? ', after the break' : ''}.</p>
            ) : (
              <button className="btn primary big" onClick={start}>
                <Play size={20} /> Watch live
              </button>
            )}
          </div>
        )}
        {relay && (
          <div className="stage-badge">
            <Radio size={14} /> Relay
          </div>
        )}
        {live && !status?.screen && (
          <div className="stage-note">
            <MonitorOff size={18} /> {child.name} isn’t sharing the screen right now.
          </div>
        )}
        {state === 'live' && pointerOn && !interactOn && status?.screen && <PointerLayer videoRef={screenRef} style={pointerStyle} send={sendPointer} />}
        {state === 'live' && interactOn && status?.screen && <ControlLayer videoRef={screenRef} send={sendInput} />}
        {state === 'connecting' && (
          <div className="stage-note">
            Connecting…
          </div>
        )}
        {live && (
          <button className="icon-btn stage-full" onClick={fullscreen} aria-label="Full screen">
            <Maximize2 size={18} />
          </button>
        )}
      </div>
      </div>

      {error && <p className="error">{error}</p>}

      {!voiceOnly && live && relay && (
        <div className="live-controls">
          <p className="muted small">
            Relay mode shows {child.name}’s screen through the server (works on restricted networks). Talking, your camera and the
            pointer aren’t available in this mode.
          </p>
          <button className="chip danger" onClick={() => stop()}>
            <PhoneOff size={16} /> Stop
          </button>
        </div>
      )}

      {!voiceOnly && live && !relay && (
        <div className="live-controls">
          <button
            className={`btn talk ${holding ? 'active' : ''}`}
            onPointerDown={(e) => {
              e.currentTarget.setPointerCapture(e.pointerId);
              setHolding(true);
            }}
            onPointerUp={() => setHolding(false)}
            onPointerCancel={() => setHolding(false)}
            onContextMenu={(e) => e.preventDefault()}
          >
            <Mic size={20} /> {holding ? 'Talking…' : 'Hold to talk'}
          </button>
          {/* Their device / My device (#930): controls grouped by whose computer they act on. */}
          <div className="control-row">
            <div className="control-group" role="group" aria-label={`${child.name}’s computer`}>
              <span className="control-group-label">Their device</span>
              <button className={`chip ${watchCam ? 'active' : ''}`} onClick={() => setWatchCam((v) => !v)} aria-pressed={watchCam} title={`See ${child.name}’s camera; they'll see the watching banner while it's on`}>
                {watchCam ? <Camera size={16} /> : <CameraOff size={16} />} Their camera
              </button>
              <button className={`chip ${hearMic ? 'active' : ''}`} onClick={() => setHearMic((v) => !v)} aria-pressed={hearMic} disabled={!status?.mic}>
                {hearMic ? <Volume2 size={16} /> : <VolumeX size={16} />} Their mic
              </button>
              <button className={`chip ${hearAudio ? 'active' : ''}`} onClick={() => setHearAudio((v) => !v)} aria-pressed={hearAudio} disabled={!status?.audio}>
                {hearAudio ? <Volume2 size={16} /> : <VolumeX size={16} />} Computer sound
              </button>
              <button
                className={`chip ${pointerOn ? 'active' : ''}`}
                onClick={() => {
                  setPointerOn((v) => !v);
                  setInteractOn(false);
                }}
                aria-pressed={pointerOn}
                disabled={!status?.screen}
                title="Point at things on their screen"
              >
                <MousePointer2 size={16} /> Pointer
              </button>
              {canInteract && (
                <button
                  className={`chip ${interactOn ? 'active' : ''}`}
                  onClick={() => {
                    setInteractOn((v) => !v);
                    setPointerOn(false);
                  }}
                  aria-pressed={interactOn}
                  disabled={!status?.screen}
                  title="Control their mouse and keyboard"
                >
                  <MousePointerClick size={16} /> Interact
                </button>
              )}
            </div>
            <div className="control-group" role="group" aria-label="Your computer">
              <span className="control-group-label">My device</span>
              <button className={`chip ${openMic ? 'active' : ''}`} onClick={() => setOpenMic((v) => !v)} aria-pressed={openMic}>
                {openMic ? <Mic size={16} /> : <MicOff size={16} />} Open mic
              </button>
              <button className={`chip ${camOn ? 'active' : ''}`} onClick={() => setCamOn((v) => !v)} aria-pressed={camOn}>
                {camOn ? <Camera size={16} /> : <CameraOff size={16} />} My camera
              </button>
              {blurOffered && (
                <button className={`chip ${blurMine ? 'active' : ''}`} onClick={() => setBlurMine(!blurMine)} aria-pressed={blurMine} title={`Blur your room on camera; ${child.name} sees only you`}>
                  <ScanFace size={16} /> Blur background
                </button>
              )}
            </div>
            <button className="chip danger control-stop" onClick={() => stop()}>
              <PhoneOff size={16} /> Stop
            </button>
          </div>
          {interactOn && status?.control === false && (
            <p className="error small" role="alert">
              {child.name}’s Mac isn’t letting ClassGuardian control it yet. On the Mac, search System Settings for “Accessibility”, open
              the list of apps allowed to control the computer, and switch on ClassGuardian (add it with + if it isn’t listed). Then turn
              Interact off and on.
            </p>
          )}
          {interactOn && (
            <p className="muted small">
              You’re controlling {child.name}’s computer: click, scroll and type on the screen above. {child.name} sees that you’re in
              control. Turn Interact off to give it back.
              {isMac && ' On a Mac, ClassGuardian needs the Accessibility permission for this.'}
            </p>
          )}
          {pointerOn && (
            <>
              <p className="muted small">
                Move or tap on {child.name}’s screen above, and they see the pointer in the same spot. Works with the ClassGuardian
                Windows and Mac apps.
              </p>
              <PointerOptions style={pointerStyle} onChange={setPointerStyle} />
            </>
          )}
        </div>
      )}
    </section>
  );
}
